import { Activity, QrCode, ScanLine, Wrench } from "lucide-react";
import { SectionEyebrow } from "@/components/Landing/primitives";

const SOLUTIONS = Object.freeze([
    {
        id: "ai-intake",
        title: "AI-powered credential intake",
        icon: ScanLine,
        body: "Workers upload tickets, licences, and certifications. Edara classifies the document, extracts holder, scope, and expiry, and flags wrong-document or lapsed uploads before they reach the compliance officer.",
    },
    {
        id: "site-access",
        title: "Live site access control",
        icon: QrCode,
        body: "QR or biometric sign-in linked to verified compliance status. Non-compliant workers cannot enter. Every entry and exit timestamped, audit-ready.",
    },
    {
        id: "plant",
        title: "Plant and equipment compliance",
        icon: Wrench,
        body: "Every item of plant on site tracked from prestart to service interval to high-risk inspection. Out-of-compliance plant is locked out automatically.",
    },
    {
        id: "sentinel",
        title: "SENTINEL Safety Intelligence",
        icon: Activity,
        body: "Real-time leading and lagging indicators, contractor scorecards with letter grades A\u2013C, critical risk control verification. Project-level data rolls up to group dashboards automatically.",
        flagship: true,
    },
]);

export default function Solution() {
    return (
        <section
            id="solutions"
            className="relative py-24 sm:py-32 bg-white text-[#0A0A0A]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl reveal">
                    <SectionEyebrow>The solution</SectionEyebrow>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1] mt-3">
                        One operating system for compliance, plant,
                        subcontractors, and safety intelligence.
                    </h2>
                    <p className="mt-5 text-lg text-zinc-600">
                        Edara Software replaces the patchwork. AI handles the
                        intake. Plant is tracked. Subcontractors are
                        accountable. SENTINEL turns operational data into
                        safety intelligence. Group HSE leadership sees what
                        matters, before it matters.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {SOLUTIONS.map((c) => (
                        <article
                            key={c.id}
                            className={`reveal lift rounded-2xl p-6 ${
                                c.flagship
                                    ? "bg-[#0A0A0A] text-white border border-[#1FA8FF]/40"
                                    : "bg-[#F8FAFC] border border-zinc-200 text-[#0A0A0A]"
                            }`}
                        >
                            <div
                                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                    c.flagship
                                        ? "bg-[#1FA8FF]/15 text-[#1FA8FF]"
                                        : "bg-[#0A0A0A] text-white"
                                }`}
                            >
                                <c.icon className="w-5 h-5" />
                            </div>
                            <h3
                                className={`mt-5 font-sora font-semibold text-lg leading-tight ${
                                    c.flagship ? "text-white" : "text-[#0A0A0A]"
                                }`}
                            >
                                {c.title}
                            </h3>
                            <p
                                className={`mt-2.5 text-[14px] leading-relaxed ${
                                    c.flagship
                                        ? "text-zinc-300"
                                        : "text-zinc-600"
                                }`}
                            >
                                {c.body}
                            </p>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
